import { useCallback } from 'react';

import { publicEnvConfig } from 'config/public';
import { TEST_MODE_COOKIE_NAME } from 'constants/cookies';
import { useInitialQueryValueContext } from 'contexts';
import { useMarketCode } from 'hooks/useSitecoreContext';
import type {
	ConnectCustomerType,
	LoginMethod,
	OauthQueryParams,
} from 'models/connect/oauth';
import {
	JULA_CONNECT_AUTHORIZE_DEFAULT_SCOPE,
	type OauthStateParam,
} from 'utils/business-logic';
import { hasCookie } from 'utils/cookies';
import { log } from 'utils/log';
import { encodeBase64 } from 'utils/string';
import { createUrl, joinPath, removeQueryString } from 'utils/url';

export function useJulaConnectTokenAuthorizeUrl() {
	const marketCode = useMarketCode();
	const { returnTo } = useInitialQueryValueContext();

	const getAuthorizeUrl = useCallback(
		({
			customerNumber,
			customerType,
			loginMethod,
		}: {
			customerNumber?: string;
			customerType: ConnectCustomerType;
			loginMethod: LoginMethod;
		}) => {
			const clientId = publicEnvConfig.JULA_CONNECT_CLIENT_ID;
			const connectOauthUrl = publicEnvConfig.JULA_CONNECT_OAUTH_URL;

			if (!clientId) {
				log.error('JULA_CONNECT_CLIENT_ID is not defined');
				return;
			}
			if (!connectOauthUrl) {
				log.error('JULA_CONNECT_OAUTH_URL is not defined');
				return;
			}
			return createUrl(joinPath(connectOauthUrl, '/connect/authorize'), {
				client_id: clientId,
				redirect_uri: `${globalThis.location.origin}/api/signin/oidc/`,
				response_type: 'code id_token',
				response_mode: 'form_post',
				nonce:
					'637568344348596225.NWExMzI0M2EtOWYyYi00ZTUyLWFmYTUtZmU2YmU2YWNmNGI1ODVkYzRjMTgtNWZiMC00MTcyLWJjZTctMmI3YmQyMzc4MjFm',
				platform: 'web',
				market: marketCode,
				preferred_type: loginMethod,
				test_mode: hasCookie(TEST_MODE_COOKIE_NAME) ? 'true' : 'false',
				state: encodeBase64(
					JSON.stringify({
						redirectPath: removeQueryString(globalThis.location.href),
						customerType,
						protectedRouteReturnPath: returnTo,
					} satisfies OauthStateParam),
				),
				customer_type: customerType,
				login_hint: customerNumber,
				scope:
					customerType === 'Unregistered'
						? 'customer.identification-number openid profile'
						: JULA_CONNECT_AUTHORIZE_DEFAULT_SCOPE,
			} satisfies OauthQueryParams);
		},
		[marketCode, returnTo],
	);
	return { getAuthorizeUrl };
}
